/**
 * Help Content Registry
 * Central registry for all help articles and categories
 */

import type { HelpArticle, HelpCategory, HelpCategoryInfo } from '../../components/help/types';

// Import institution help content
import { gettingStartedArticles } from './institution/getting-started';
import { managingRecordsArticles } from './institution/managing-records';
import { pfileManagementArticles } from './institution/pfile-management';
import { workflowApprovalsArticles } from './institution/workflow-approvals';
import { searchReportsArticles } from './institution/search-reports';
import { bulkImportArticles } from './institution/bulk-import';

// Import platform help content
import { mdaManagementArticles } from './platform/mda-management';
import { userManagementArticles } from './platform/user-management';
import { configurationArticles } from './platform/configuration';
import { analyticsArticles } from './platform/analytics';

// Import common help content
import { keyboardShortcutsArticles } from './common/keyboard-shortcuts';
import { glossaryArticles } from './common/glossary';
import { faqArticles } from './common/faq';

// Category definitions with icons
export const categories: HelpCategoryInfo[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    description: 'First steps for new users',
    type: 'institution',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'records',
    name: 'Records Management',
    description: 'Managing documents and folders',
    type: 'institution',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
  },
  {
    id: 'pfiles',
    name: 'Personnel Files',
    description: 'Staff PFile jacket management',
    type: 'institution',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    id: 'workflows',
    name: 'Workflows',
    description: 'Approval workflows and routing',
    type: 'institution',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
  {
    id: 'search',
    name: 'Search & Reports',
    description: 'Finding and reporting on records',
    type: 'institution',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    ),
  },
  {
    id: 'bulk-import',
    name: 'Bulk Import',
    description: 'Importing data in bulk',
    type: 'institution',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
  },
  {
    id: 'mda-management',
    name: 'MDA Management',
    description: 'Managing MDAs and institutions',
    type: 'platform',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: 'user-management',
    name: 'User Management',
    description: 'Managing platform users',
    type: 'platform',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: 'configuration',
    name: 'Configuration',
    description: 'System configuration options',
    type: 'platform',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Platform analytics and insights',
    type: 'platform',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
  {
    id: 'shortcuts',
    name: 'Keyboard Shortcuts',
    description: 'Quick access shortcuts',
    type: 'common',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <line x1="6" y1="10" x2="6" y2="10.01" />
        <line x1="10" y1="10" x2="10" y2="10.01" />
        <line x1="14" y1="10" x2="14" y2="10.01" />
        <line x1="18" y1="10" x2="18" y2="10.01" />
        <line x1="8" y1="14" x2="16" y2="14" />
      </svg>
    ),
  },
  {
    id: 'glossary',
    name: 'Glossary',
    description: 'Terms and definitions',
    type: 'common',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  {
    id: 'faq',
    name: 'FAQ',
    description: 'Frequently asked questions',
    type: 'common',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
];

// Combine all articles
export const allArticles: HelpArticle[] = [
  ...gettingStartedArticles,
  ...managingRecordsArticles,
  ...pfileManagementArticles,
  ...workflowApprovalsArticles,
  ...searchReportsArticles,
  ...bulkImportArticles,
  ...mdaManagementArticles,
  ...userManagementArticles,
  ...configurationArticles,
  ...analyticsArticles,
  ...keyboardShortcutsArticles,
  ...glossaryArticles,
  ...faqArticles,
];

// Helper functions
export function getCategoryInfo(categoryId: HelpCategory): HelpCategoryInfo | undefined {
  return categories.find(cat => cat.id === categoryId);
}

export function getArticlesByCategory(categoryId: HelpCategory): HelpArticle[] {
  return allArticles
    .filter(article => article.category === categoryId)
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
}

export function getArticleById(articleId: string): HelpArticle | undefined {
  return allArticles.find(article => article.id === articleId);
}
